// Font optimization utilities for lite mode
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';

// System font stacks that match brand font metrics
export const SYSTEM_FONT_STACKS = {
  // For Agrandir (sans-serif with similar x-height)
  agrandir: [
    'system-ui',
    '-apple-system', 
    'BlinkMacSystemFont',
    'Segoe UI',
    'Roboto',
    'Arial',
    'sans-serif'
  ].join(', '),
  
  // For Monoton (display/decorative - fallback to system sans)
  monoton: [
    'Impact',
    'Arial Black',
    'system-ui',
    'sans-serif'
  ].join(', '),
  
  // Default system stack for body text
  body: [
    'system-ui',
    '-apple-system',
    'BlinkMacSystemFont',
    'Segoe UI',
    'Roboto',
    'Arial',
    'sans-serif'
  ].join(', ')
} as const;

// Progressive font loading based on connection mode
export const useFontStrategy = () => {
  const mode = useConnectionMode();
  
  const shouldLoadBrandFonts = mode === 'full';
  const shouldUseFallbacks = mode === 'lite' || mode === 'offline';
  
  return {
    shouldLoadBrandFonts,
    shouldUseFallbacks,
    getAgrandirStack: () => shouldUseFallbacks 
      ? SYSTEM_FONT_STACKS.agrandir 
      : `Agrandir, ${SYSTEM_FONT_STACKS.agrandir}`,
    getMonotonStack: () => shouldUseFallbacks
      ? SYSTEM_FONT_STACKS.monoton
      : `Monoton, ${SYSTEM_FONT_STACKS.monoton}`,
    getBodyStack: () => SYSTEM_FONT_STACKS.body
  };
};

// Lazy load fonts after TTI or in full mode
export const lazyLoadBrandFonts = () => {
  if (typeof window === 'undefined') return;
  
  // Check if already loaded
  if (document.querySelector('link[data-brand-fonts="loaded"]')) {
    return;
  }
  
  // Load Agrandir (main brand font)
  const agrandirLink = document.createElement('link');
  agrandirLink.rel = 'stylesheet';
  agrandirLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
  agrandirLink.setAttribute('data-brand-fonts', 'agrandir');
  
  // Load fonts with low priority
  agrandirLink.onload = () => {
    document.documentElement.classList.add('fonts-loaded');
  };
  
  document.head.appendChild(agrandirLink);
  
  // Mark as loaded
  const marker = document.createElement('link');
  marker.setAttribute('data-brand-fonts', 'loaded');
  document.head.appendChild(marker);
};

// Initialize font loading strategy
export const initFontOptimization = (mode: string) => {
  if (typeof window === 'undefined') return;
  
  // In lite mode, ensure system fonts are prioritized
  if (mode === 'lite' || mode === 'offline') {
    document.documentElement.style.setProperty('--font-loading', 'system');
  } else {
    // In full mode, load brand fonts
    document.documentElement.style.setProperty('--font-loading', 'brand');
    
    // Use requestIdleCallback if available, or setTimeout as fallback
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => lazyLoadBrandFonts(), { timeout: 2000 });
    } else {
      setTimeout(lazyLoadBrandFonts, 100);
    }
  }
};